import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImagePlus, Download, Copy, Trash, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { InternalPageHeader } from "@/components/ui/internal-page-header";

// Define the image type
interface GeneratedImage {
  id: string;
  imageUrl: string;
  prompt: string;
  createdAt: string;
}

const IMAGES_PER_PAGE = 9;

export default function ImageGeneration() {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [imageHistory, setImageHistory] = useState<GeneratedImage[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [credits, setCredits] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const { user, isLoading, subscriptionTier } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch user's image history when component mounts or page changes
  useEffect(() => {
    if (user) {
      fetchImageHistory();
    }
  }, [user, currentPage]);

  // Fetch user credits
  useEffect(() => {
    const fetchCredits = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('credits')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching credits:', error);
        return;
      }
      
      setCredits(data?.credits ?? 0);
    };

    fetchCredits();
  }, [user]);

  const fetchImageHistory = async () => {
    try {
      setIsLoadingHistory(true);

      // First, get total count
      const { count, error: countError } = await supabase
        .from('generated_images')
        .select('*', { count: 'exact', head: true });

      if (countError) throw countError;
      setTotalImages(count || 0);

      // Then fetch paginated data
      const { data, error } = await supabase
        .from('generated_images')
        .select('*')
        .order('created_at', { ascending: false })
        .range(currentPage * IMAGES_PER_PAGE, (currentPage + 1) * IMAGES_PER_PAGE - 1);
      
      if (error) {
        throw error;
      }

      if (data) {
        const formattedData = data.map(item => ({
          id: item.id,
          imageUrl: item.image_url,
          prompt: item.prompt,
          createdAt: new Date(item.created_at).toLocaleString()
        }));
        
        setImageHistory(formattedData);
      }
    } catch (error) {
      console.error('Error in fetchImageHistory:', error);
      toast({
        title: "Error",
        description: "Failed to load your image history.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const generateImage = async () => {
    if (!prompt.trim()) return;
    
    setIsGenerating(true);
    
    try {
      const { data: functionData, error: functionError } = await supabase.functions.invoke('generate-image', {
        body: { prompt: prompt.trim() }
      });

      if (functionError) {
        console.error('Function error:', functionError);
        toast({
          title: "Generation Failed",
          description: "There was an error generating your image. Please try again.",
          variant: "destructive"
        });
        return;
      }

      if (functionData.error) {
        if (functionData.error === 'Insufficient credits') {
          toast({
            title: "No Credits Remaining",
            description: "Please subscribe to get more credits.",
            variant: "destructive"
          });
          navigate('/pricing');
          return;
        }
        
        console.error('API error:', functionData.error);
        toast({
          title: "Generation Failed",
          description: functionData.error.message || "Failed to generate image",
          variant: "destructive"
        });
        return;
      }

      // Update credits from the response
      if (functionData.remainingCredits !== undefined) {
        setCredits(functionData.remainingCredits);
      }

      // Add the new image to our local state
      const newImage: GeneratedImage = {
        id: functionData.id,
        imageUrl: functionData.imageUrl,
        prompt: prompt,
        createdAt: new Date().toLocaleString()
      };
      
      setImages(prevImages => [newImage, ...prevImages]);
      setPrompt("");
      
      toast({
        title: "Image Generated",
        description: "Your image has been successfully created!",
      });

      // Refresh image history
      fetchImageHistory();
    } catch (error) {
      console.error('Error generating image:', error);
      toast({
        title: "Error",
        description: "Failed to generate image. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (imageUrl: string, promptText: string) => {
    try {
      // Create a temporary link and trigger download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = imageUrl;
      
      const filename = `${promptText.substring(0, 20).replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      toast({
        title: "Download Started",
        description: "Your image download has started.",
      });
    } catch (error) {
      console.error('Error downloading image:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download the image. Please try again.",
        variant: "destructive"
      });
    }
  };

  const copyPromptToClipboard = (promptText: string) => {
    navigator.clipboard.writeText(promptText);
    toast({
      title: "Copied",
      description: "Prompt copied to clipboard.",
    });
  };

  const deleteImage = async (imageId: string) => {
    try {
      const { error } = await supabase
        .from('generated_images')
        .delete()
        .eq('id', imageId);

      if (error) throw error;

      // Update local state
      setImageHistory(prev => prev.filter(img => img.id !== imageId));
      setImages(prev => prev.filter(img => img.id !== imageId));

      toast({
        title: "Image Deleted",
        description: "The image has been removed from your history.",
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete the image.",
        variant: "destructive"
      });
    }
  };

  const regenerateImage = async (promptText: string) => {
    setPrompt(promptText);
    await generateImage();
  };

  const totalPages = Math.ceil(totalImages / IMAGES_PER_PAGE);

  return (
    <div className="container mx-auto px-4 py-8">
      <InternalPageHeader 
        title="AI Image Generation" 
        description="Transform your ideas into stunning visuals with our AI-powered image generation." 
      />
      {credits !== null && (
        <div className="mb-4 text-center text-muted-foreground">
          You have <span className="font-semibold">{credits}</span> credits remaining.
        </div>
      )}
      <div className="mb-8">
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Describe the image you want to generate..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && generateImage()}
                  disabled={isGenerating}
                />
              </div>
              <Button 
                onClick={generateImage} 
                disabled={isGenerating || !prompt.trim() || credits === 0}
                className="min-w-[120px]"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating
                  </>
                ) : (
                  <>
                    <ImagePlus className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>

            {credits === 0 && (
              <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg">
                <p className="text-sm font-medium">Need more credits?</p>
                <Button variant="default" size="sm" onClick={() => navigate('/pricing')}>
                  Subscribe Now
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Generated Images */}
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Image History</h2>
          {totalPages > 1 && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Page {currentPage + 1} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage === totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {isLoadingHistory ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: IMAGES_PER_PAGE }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="aspect-square" />
                <div className="p-4">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-4" />
                  <div className="flex justify-end gap-2">
                    {Array.from({ length: 4 }).map((_, j) => (
                      <Skeleton key={j} className="h-8 w-16" />
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : imageHistory.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {imageHistory.map((image) => (
              <Card key={image.id} className="overflow-hidden">
                <div className="aspect-square relative bg-muted">
                  <img 
                    src={image.imageUrl} 
                    alt={image.prompt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {image.prompt}
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyPromptToClipboard(image.prompt)}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => regenerateImage(image.prompt)}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Retry
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDownload(image.imageUrl, image.prompt)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deleteImage(image.id)}
                    >
                      <Trash className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <ImagePlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Images Yet</h3>
            <p className="text-muted-foreground">
              Enter a prompt above to generate your first AI image.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
} 